import ActionButtonRounded from 'Components/ActionButtonRounded';
import ConfirmationDialog from 'Components/ConfirmationDialog';
import { WritePermissionContainer } from 'Containers/ReadWritePermissions';
import * as React from 'react';
import { useAppSelector } from 'Store/index';
import ActionButton from '../ActionButton';

const ConditionalWritePermissionContainer = ({
  condition,
  document,
  buttonProps,
  children,
}: {
  condition: boolean;
  document?: IControlProps;
  buttonProps: React.ComponentProps<typeof ActionButton>;
  children: React.JSX.Element;
}): React.JSX.Element =>
  condition ? (
    <WritePermissionContainer
      document={document}
      disabled={buttonProps.disabled}
      disabledTooltip={buttonProps.disabledTooltip}
    >
      {children}
    </WritePermissionContainer>
  ) : (
    children
  );

const DocumentCreationButton = ({
  name,
  document,
  newStyle,
  creationConfirmationText,
  creationConfirmationButtons,
  enabled,
  onClick,
  asyncOnClick,
  ...buttonProps
}: IDocumentCreationButton): React.JSX.Element => {
  const [confirmationDialogOpen, setConfirmationDialogOpen] = React.useState<boolean>(false);
  // oxlint-disable-next-line react/hook-use-state
  const [_loading, _setLoading] = React.useState<boolean>(false);

  const isCreating = useAppSelector(s => s.documents.isCreating);

  const loading = document ? isCreating?.id === document._id : isCreating?.name === name && isCreating.id === 'noid';

  const onClickFn = (event: React.MouseEvent): void => {
    if (_loading) return;
    if (loading) return;
    if (buttonProps.disabled) return;

    if (onClick) onClick(event);
    else if (asyncOnClick) {
      _setLoading(true);
      asyncOnClick(event)
        .then(() => null)
        // eslint-disable-next-line no-console
        .catch((e: unknown) => console.error(e))
        .finally(() => _setLoading(false));
    }
  };

  return (
    <>
      <ConditionalWritePermissionContainer condition={!enabled} document={document} buttonProps={buttonProps}>
        {newStyle ? (
          <ActionButtonRounded
            loading={loading}
            {...{
              ...buttonProps,
              onClick: creationConfirmationText ? () => setConfirmationDialogOpen(true) : onClickFn,
            }}
          />
        ) : (
          <ActionButton
            loading={loading}
            {...{
              ...buttonProps,
              onClick: creationConfirmationText ? () => setConfirmationDialogOpen(true) : onClickFn,
            }}
          />
        )}
      </ConditionalWritePermissionContainer>
      {creationConfirmationText && confirmationDialogOpen && (
        <ConfirmationDialog
          open={confirmationDialogOpen}
          text={creationConfirmationText}
          cancel={{
            callback: creationConfirmationButtons?.cancel?.callback ?? (() => setConfirmationDialogOpen(false)),
            text: creationConfirmationButtons?.cancel?.text,
          }}
          confirm={{
            callback: creationConfirmationButtons?.confirm?.callback ?? onClickFn,
            text: creationConfirmationButtons?.confirm?.text,
            loading,
          }}
        />
      )}
    </>
  );
};

type IDocumentCreationButton = CommonButtonProps & {
  name: string;
  document?: IControlProps;
  newStyle?: boolean;
  creationConfirmationText?: React.JSX.Element | string;
  creationConfirmationButtons?: {
    cancel?: { callback?: () => void; text?: React.JSX.Element | string };
    confirm?: { callback?: (() => void) | ((e: React.MouseEvent<Element, MouseEvent>) => void) | null; text?: string };
  };
  enabled?: boolean;
};

export default DocumentCreationButton;
